// Cases on the screen: the open list, the closed list (pages), one Case, and
// every write (one PATCH entry, the same input as MCP's update).
//
// Promise:
// - A write is shown at once on the Case and on the rows of the open list, with
//   the same chain rules the server applies (`@trameria/core/cases-shared`),
//   and put back when the server refuses it. The answer of the server then
//   replaces the Case; the lists are fetched again.
// - A created Case is in the cache before its screen is shown (no wait).
// - The context is not written here: the screen edits its live copy.
import {
  infiniteQueryOptions,
  keepPreviousData,
  type QueryClient,
  type QueryKey,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  ballOfSteps,
  type CaseDetail,
  type CaseList,
  type CaseRelated,
  type CaseStatus,
  type CaseStep,
  type CaseStepKind,
  type CaseSummary,
  currentStepIds,
  defaultParentIds,
  lastClosedStepId,
  planFields,
} from "@trameria/core/cases-shared";
import { useApi } from "../app/context";
import { useMeWorkspace } from "../app/workspace-filter";
import type { Api } from "../data/api";
import { idempotencyKeyFor } from "../data/api";
import { newOptimisticId } from "../data/optimistic";

// ---- keys and reads --------------------------------------------------------------

export const casesPrefix = ["me", "cases"] as const;
export const caseListsPrefix = [...casesPrefix, "list"] as const;
const openListPrefix = [...caseListsPrefix, "open"] as const;
export const caseKey = (id: string) => [...casesPrefix, "detail", id] as const;

const workspaceParam = (workspace: string | null) =>
  workspace === null ? "" : `&workspace=${encodeURIComponent(workspace)}`;

export const openCasesQuery = (api: Api, workspace: string | null) =>
  queryOptions({
    queryKey: [...openListPrefix, workspace ?? ""] as const,
    queryFn: () =>
      api<CaseList>(`/api/me/cases?status=open${workspaceParam(workspace)}`),
  });

/** The closed Cases, page by page; with typed words, those whose title or Context holds them. */
export const closedCasesQuery = (
  api: Api,
  workspace: string | null,
  q?: string,
) =>
  infiniteQueryOptions({
    queryKey: [...caseListsPrefix, "closed", workspace ?? "", q ?? ""] as const,
    queryFn: ({ pageParam }) =>
      api<CaseList>(
        `/api/me/cases?status=closed${workspaceParam(workspace)}${
          q ? `&q=${encodeURIComponent(q)}` : ""
        }${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ""}`,
      ),
    initialPageParam: "",
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });

export const caseQuery = (api: Api, id: string) =>
  queryOptions({
    queryKey: caseKey(id),
    queryFn: () => api<CaseDetail>(`/api/me/cases/${encodeURIComponent(id)}`),
  });

/**
 * The name of the context's live copy. It changes only when the copy is
 * rebuilt, which a refused connection tells (the screen fetches it again then).
 */
export const caseContextLiveQuery = (api: Api, id: string) =>
  queryOptions({
    queryKey: [...casesPrefix, "context-live", id] as const,
    queryFn: () =>
      api<{ name: string }>(
        `/api/me/cases/${encodeURIComponent(id)}/context-live`,
      ),
    staleTime: Number.POSITIVE_INFINITY,
  });

// ---- the update input ------------------------------------------------------------

export interface StepAddInput {
  kind: CaseStepKind;
  title: string;
  body?: string;
  wait_kind?: string;
  wait_id?: string;
  wait_until?: string | null;
  due_at?: string | null;
  /** The parents; without them the server decides (and marks it unplaced). */
  parents?: string[];
}

/**
 * Where the screen puts a step it adds (the owner's decision): beside the
 * current steps, after the step closed last; the first step is a root.
 */
export function screenParents(steps: readonly CaseStep[]): string[] {
  const last = lastClosedStepId(steps);
  return last ? [last] : [];
}

export interface StepUpdateInput {
  id: string;
  title?: string;
  body?: string;
  status?: CaseStatus;
  result?: string | null;
  wait_until?: string | null;
  due_at?: string | null;
  plan?: { scope: "day" | "week"; on: string } | "someday" | null;
}

export interface CasePatch {
  title?: string;
  // No `note`: the screen writes the context through its live copy.
  status?: CaseStatus;
  /** Label ids put on and taken off. */
  add_labels?: string[];
  remove_labels?: string[];
  update_steps?: StepUpdateInput[];
  add_steps?: StepAddInput[];
  delete_steps?: string[];
  add_links?: { kind: string; target_id: string; label?: string }[];
  remove_links?: string[];
}

/** What the optimistic view needs beyond the input (names the server knows). */
export interface PatchView {
  /** The wait target's name of an added waiting. */
  waitLabel?: string;
}

// ---- the optimistic view -----------------------------------------------------------

function withChain(steps: CaseStep[]): CaseStep[] {
  const current = currentStepIds(steps);
  return steps.map((s) => ({ ...s, current: current.has(s.id) }));
}

/** The Case as it will be after `patch`, by the rules the server applies. */
export function applyCasePatch(
  c: CaseDetail,
  patch: CasePatch,
  view: PatchView = {},
  now: Date = new Date(),
): CaseDetail {
  const at = now.toISOString();
  let next: CaseDetail = { ...c };
  let steps = [...c.steps];
  if (patch.title !== undefined) next.title = patch.title;
  if (patch.add_labels || patch.remove_labels) {
    const off = new Set(patch.remove_labels ?? []);
    next.labelIds = [
      ...new Set([
        ...c.labelIds.filter((id) => !off.has(id)),
        ...(patch.add_labels ?? []),
      ]),
    ];
  }
  for (const u of patch.update_steps ?? []) {
    steps = steps.map((s) => {
      if (s.id !== u.id) return s;
      const out: CaseStep = { ...s };
      if (u.title !== undefined) out.title = u.title;
      if (u.body !== undefined) out.body = u.body;
      if (u.result !== undefined) out.result = u.result;
      if (u.wait_until !== undefined) out.waitUntil = u.wait_until;
      if (u.due_at !== undefined) out.dueAt = u.due_at;
      if (u.plan !== undefined) Object.assign(out, planFields(u.plan));
      if (u.status !== undefined && u.status !== s.status) {
        out.status = u.status;
        out.closedAt = u.status === "open" ? null : at;
        if (u.status === "open") {
          out.autoClosedAt = null;
          next.status = "open";
          next.closedAt = null;
        }
      }
      return out;
    });
  }
  for (const id of patch.delete_steps ?? []) {
    const gone = steps.find((s) => s.id === id);
    if (!gone) continue;
    steps = steps
      .filter((s) => s.id !== id)
      .map((s) =>
        s.parentIds.includes(id)
          ? {
              ...s,
              parentIds: [
                ...new Set([
                  ...s.parentIds.filter((p) => p !== id),
                  ...gone.parentIds,
                ]),
              ],
            }
          : s,
      );
  }
  for (const add of patch.add_steps ?? []) {
    const parentIds =
      add.parents ??
      defaultParentIds(steps, []) ??
      [lastClosedStepId(steps)].filter((x): x is string => x !== null);
    steps.push({
      id: newOptimisticId(),
      kind: add.kind,
      title: add.title,
      body: add.body ?? "",
      result: null,
      status: "open",
      parentIds,
      current: false,
      unplaced: add.parents === undefined && steps.length > 0,
      waitKind: add.wait_kind ?? null,
      waitId: add.wait_id ?? null,
      waitTarget:
        add.wait_kind && add.wait_id
          ? {
              kind: add.wait_kind,
              id: add.wait_id,
              visible: true,
              label: view.waitLabel ?? "",
              known: true,
            }
          : null,
      waitMine: false,
      waitUntil: add.wait_until ?? null,
      dueAt: add.due_at ?? null,
      plan: null,
      someday: false,
      links: { body: [], result: [] },
      files: { body: [], result: [] },
      source: "manual",
      createdAt: at,
      closedAt: null,
      autoClosedAt: null,
      waitTargetDroppedAt: null,
    });
    next.status = "open";
    next.closedAt = null;
  }
  if (patch.status !== undefined && patch.status !== c.status) {
    next.status = patch.status;
    next.closedAt = patch.status === "open" ? null : at;
    if (patch.status !== "open")
      steps = steps.map((s) =>
        s.status === "open"
          ? { ...s, status: patch.status as CaseStatus, closedAt: at }
          : s,
      );
  }
  let related: CaseRelated[] = c.related;
  for (const l of patch.add_links ?? [])
    if (!related.some((r) => r.kind === l.kind && r.targetId === l.target_id))
      related = [
        ...related,
        {
          kind: l.kind,
          targetId: l.target_id,
          target: {
            kind: l.kind,
            id: l.target_id,
            visible: true,
            label: l.label ?? l.target_id,
            known: true,
          },
          sources: [{ from: "manual", linkId: newOptimisticId() }],
        },
      ];
  for (const linkId of patch.remove_links ?? [])
    related = related.flatMap((r) => {
      const sources = r.sources.filter(
        (s) => !(s.from === "manual" && s.linkId === linkId),
      );
      if (sources.length === r.sources.length) return [r];
      return sources.length > 0 ? [{ ...r, sources }] : [];
    });
  steps = withChain(steps);
  next = {
    ...next,
    steps,
    related,
    openSteps: steps.filter((s) => s.status === "open"),
    updatedAt: at,
  };
  next.ball = ballOfSteps(steps, now.getTime());
  return next;
}

/** The list row of a Case (open steps only). */
function summaryOf(c: CaseDetail): CaseSummary {
  return {
    id: c.id,
    title: c.title,
    status: c.status,
    ball: c.ball,
    label: c.label,
    workspaceId: c.workspaceId,
    labelIds: c.labelIds,
    openSteps: c.steps.filter((s) => s.status === "open"),
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
    closedAt: c.closedAt,
    ...(c.ext ? { ext: c.ext } : {}),
  };
}

/** Puts a Case's new state on the rows of every open list it is in. */
function putOnOpenLists(qc: QueryClient, c: CaseDetail) {
  qc.setQueriesData<CaseList>({ queryKey: openListPrefix }, (old) => {
    if (!old) return old;
    const items =
      c.status === "open"
        ? old.items.map((x) => (x.id === c.id ? summaryOf(c) : x))
        : old.items.filter((x) => x.id !== c.id);
    return { ...old, items };
  });
}

interface Snapshot {
  detail: CaseDetail | undefined;
  lists: [QueryKey, CaseList | undefined][];
}

async function snapshot(qc: QueryClient, id: string): Promise<Snapshot> {
  await qc.cancelQueries({ queryKey: caseKey(id) });
  await qc.cancelQueries({ queryKey: openListPrefix });
  return {
    detail: qc.getQueryData<CaseDetail>(caseKey(id)),
    lists: qc.getQueriesData<CaseList>({ queryKey: openListPrefix }),
  };
}

function restore(qc: QueryClient, id: string, s: Snapshot | undefined) {
  if (!s) return;
  qc.setQueryData(caseKey(id), s.detail);
  for (const [key, value] of s.lists) qc.setQueryData(key, value);
}

const invalidateLists = (qc: QueryClient) =>
  qc.invalidateQueries({ queryKey: caseListsPrefix });

// ---- writes --------------------------------------------------------------------------

export interface UpdateCaseInput {
  caseId: string;
  patch: CasePatch;
  view?: PatchView;
}

export function useUpdateCase() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCaseInput) =>
      api<CaseDetail>(`/api/me/cases/${encodeURIComponent(input.caseId)}`, {
        method: "PATCH",
        body: input.patch,
      }),
    onMutate: async (input: UpdateCaseInput) => {
      const before = await snapshot(qc, input.caseId);
      if (before.detail) {
        const after = applyCasePatch(before.detail, input.patch, input.view);
        qc.setQueryData(caseKey(input.caseId), after);
        putOnOpenLists(qc, after);
      }
      return before;
    },
    onError: (_error, input, before) => {
      restore(qc, input.caseId, before);
    },
    onSuccess: (detail, input) => {
      qc.setQueryData(caseKey(input.caseId), detail);
    },
    onSettled: () => invalidateLists(qc),
  });
}

export interface CreateCaseInput {
  title: string;
  // No `note`: what was written before the Case existed joins its live copy.
  first_step?: StepAddInput;
}

export function useCreateCase() {
  const api = useApi();
  const qc = useQueryClient();
  // A Case made while Me is narrowed to a Workspace belongs to it.
  const [workspace] = useMeWorkspace();
  return useMutation({
    mutationFn: (input: CreateCaseInput) =>
      api<CaseDetail>("/api/me/cases", {
        method: "POST",
        body: workspace ? { ...input, workspace } : input,
        idempotencyKey: idempotencyKeyFor(input),
      }),
    onSuccess: (detail) => {
      qc.setQueryData(caseKey(detail.id), detail);
    },
    onSettled: () => invalidateLists(qc),
  });
}

/**
 * Deleting leaves the Case's screen at once, so a refusal is told by `onFailed`
 * (given here, not per call: the screen that asked is gone by then).
 */
export function useDeleteCase(
  onFailed?: (error: Error, input: { caseId: string; title: string }) => void,
) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { caseId: string; title: string }) =>
      api(`/api/me/cases/${encodeURIComponent(input.caseId)}`, {
        method: "DELETE",
      }),
    onMutate: async (input) => {
      const before = await snapshot(qc, input.caseId);
      qc.setQueriesData<CaseList>({ queryKey: openListPrefix }, (old) =>
        old
          ? { ...old, items: old.items.filter((x) => x.id !== input.caseId) }
          : old,
      );
      return before;
    },
    onError: (error, input, before) => {
      restore(qc, input.caseId, before);
      onFailed?.(error, input);
    },
    onSuccess: (_d, input) =>
      qc.removeQueries({ queryKey: caseKey(input.caseId) }),
    onSettled: () => invalidateLists(qc),
  });
}

export function useRemoveCaseFile() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { caseId: string; fileId: string; name: string }) =>
      api<CaseDetail>(
        `/api/me/cases/${encodeURIComponent(input.caseId)}/files/${encodeURIComponent(input.fileId)}`,
        { method: "DELETE" },
      ),
    onSuccess: (detail, input) =>
      qc.setQueryData(caseKey(input.caseId), detail),
  });
}

// ---- realtime -----------------------------------------------------------------------

export function caseChanged(qc: QueryClient, caseId: string | undefined) {
  if (caseId) void qc.invalidateQueries({ queryKey: caseKey(caseId) });
  void invalidateLists(qc);
}

export function caseDeleted(qc: QueryClient, caseId: string | undefined) {
  if (caseId) qc.removeQueries({ queryKey: caseKey(caseId) });
  void invalidateLists(qc);
}

/** Labels changed: every Case shown may carry a removed one. */
export const invalidateCases = (qc: QueryClient) =>
  qc.invalidateQueries({ queryKey: casesPrefix });
